// components/RichTextCollaborativeEditor.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { Collaboration } from '@tiptap/extension-collaboration';
import { CollaborationCursor } from '@tiptap/extension-collaboration-cursor';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import * as Y from 'yjs';
import { Awareness } from 'y-protocols/awareness';
import io, { Socket } from 'socket.io-client';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Link as LinkIcon,
  Image as ImageIcon,
  Table as TableIcon
} from 'lucide-react';
import { SOCKET_URL as SOCKET_URL_CONFIG } from '../config/api';

const SOCKET_URL = SOCKET_URL_CONFIG || window.location.origin;

interface RichTextCollaborativeEditorProps {
  projectId: string;
  user: {
    _id: string;
    name: string;
    email: string;
  };
  initialContent?: string;
  onContentChange?: (content: string) => void;
  onUsersChange?: (users: Array<{ userId: string; userName: string }>) => void;
}

const RichTextCollaborativeEditor: React.FC<RichTextCollaborativeEditorProps> = ({
  projectId,
  user,
  initialContent = '',
  onContentChange,
  onUsersChange
}) => {
  const [yjsReady, setYjsReady] = useState(false);
  const ydocRef = useRef<Y.Doc | null>(null);
  const awarenessRef = useRef<Awareness | null>(null);
  const yXmlFragmentRef = useRef<Y.XmlFragment | null>(null);

  // Initialiser Yjs AVANT de créer l'éditeur
  useEffect(() => {
    const ydoc = new Y.Doc();
    ydocRef.current = ydoc;

    const yXmlFragment = ydoc.getXmlFragment('prosemirror');
    yXmlFragmentRef.current = yXmlFragment;

    const awareness = new Awareness(ydoc);
    awarenessRef.current = awareness;

    // Initialiser l'état local de l'utilisateur
    if (awarenessRef.current) {
      awarenessRef.current.setLocalStateField('user', {
        name: user.name,
        color: '#3b82f6',
      });
    }

    setYjsReady(true);

    return () => {
      if (awarenessRef.current) {
        awarenessRef.current.destroy();
      }
    };
  }, [user.name]);

  const editor = useEditor({
    extensions: yjsReady && yXmlFragmentRef.current && awarenessRef.current ? [
      StarterKit,
      Underline,
      Collaboration.configure({
        fragment: yXmlFragmentRef.current,
      }),
      // CollaborationCursor nécessite un provider (HocuspocusProvider)
      // Pour l'instant, on utilise seulement Collaboration pour la synchronisation du contenu
      // CollaborationCursor sera ajouté plus tard avec un provider approprié
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
      }),
    ] : [
      StarterKit,
      Underline,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
      }),
    ],
    content: initialContent,
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-full p-6',
      },
    },
    // Ne créer l'éditeur que quand Yjs est complètement initialisé
    immediatelyRender: false,
  }, [yjsReady, initialContent, user.name]);

  // Référence pour Socket.io
  const socketRef = useRef<any>(null);

  // Utiliser useRef pour onContentChange pour éviter les re-renders
  const onContentChangeRef = useRef(onContentChange);
  useEffect(() => {
    onContentChangeRef.current = onContentChange;
  }, [onContentChange]);

  // Synchronisation Yjs via Socket.io pour collaboration temps réel
  useEffect(() => {
    if (!editor || !yjsReady || !ydocRef.current || !yXmlFragmentRef.current) {
      console.log('[RichEditor] ⏳ En attente de l\'initialisation (editor:', !!editor, 'yjsReady:', yjsReady, 'ydoc:', !!ydocRef.current, 'fragment:', !!yXmlFragmentRef.current);
      return;
    }

    const ydoc = ydocRef.current;
    const yXmlFragment = yXmlFragmentRef.current;

    console.log('[RichEditor] 🚀 Initialisation synchronisation Yjs via Socket.io');

    // Initialiser Socket.io pour Yjs
    const token = localStorage.getItem('token');
    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    const roomId = `file-collab-${projectId}`;

    socket.on('connect', () => {
      console.log('[RichEditor] ✅ Socket connecté pour Yjs, socket.id:', socket.id);
      socket.emit('file-collab:join', { projectId, roomId });
      
      // Attendre un peu avant de demander la synchronisation
      setTimeout(() => {
        // Demander l'état initial Yjs
        const yjsState = Y.encodeStateAsUpdate(ydoc);
        console.log('[RichEditor] 📤 Demande synchronisation Yjs, état actuel:', yjsState.length, 'bytes');
        socket.emit('yjs-sync', {
          projectId,
          fileId: 'collaborative-file',
          state: Array.from(yjsState)
        });
        
        // Vérifier que le socket est bien dans la room
        console.log('[RichEditor] 🔍 Vérification room:', roomId);
      }, 500);
    });
    
    // Écouter la confirmation de join pour vérifier que tout est OK
    socket.on('file-collab:joined', (data: any) => {
      console.log('[RichEditor] ✅ Rejoint la room de collaboration, utilisateurs actifs:', data.activeUsers?.length || 0);
    });

    socket.on('connect_error', (error) => {
      console.error('[RichEditor] ❌ Erreur connexion Socket.io:', error);
    });

    socket.on('disconnect', (reason) => {
      console.warn('[RichEditor] ⚠️ Socket déconnecté:', reason);
    });

    // Écouter les mises à jour Yjs du serveur
    socket.on('yjs-update', (data: { update: number[]; projectId?: string }) => {
      // Vérifier que c'est pour ce projet
      if (data.projectId && data.projectId !== projectId) {
        console.log('[RichEditor] Mise à jour Yjs ignorée (mauvais projet)');
        return;
      }
      
      if (data.update && data.update.length > 0) {
        try {
          console.log('[RichEditor] ✅ Réception mise à jour Yjs du serveur, taille:', data.update.length, 'bytes');
          
          // Vérifier l'état avant l'application
          const beforeState = Y.encodeStateAsUpdate(ydoc);
          
          // Utiliser 'remote' comme origine pour que handleYjsUpdate ne renvoie pas la mise à jour
          // L'extension Collaboration de Tiptap détectera automatiquement cette mise à jour
          Y.applyUpdate(ydoc, new Uint8Array(data.update), 'remote');
          
          // Vérifier l'état après l'application
          const afterState = Y.encodeStateAsUpdate(ydoc);
          const hasChanged = beforeState.length !== afterState.length || 
                            !beforeState.every((val, idx) => val === afterState[idx]);
          
          if (hasChanged) {
            console.log('[RichEditor] ✅ Mise à jour Yjs appliquée avec succès (état modifié)');
          } else {
            console.log('[RichEditor] ⚠️ Mise à jour Yjs appliquée mais état inchangé (peut être normal)');
          }
        } catch (err) {
          console.error('[RichEditor] ❌ Erreur application update Yjs:', err);
        }
      } else {
        console.warn('[RichEditor] ⚠️ Mise à jour Yjs vide reçue');
      }
    });

    // Écouter les synchronisations d'état Yjs
    socket.on('yjs-sync', (data: { state: number[]; projectId?: string }) => {
      // Vérifier que c'est pour ce projet
      if (data.projectId && data.projectId !== projectId) {
        return;
      }
      
      if (data.state && data.state.length > 0) {
        try {
          console.log('[RichEditor] Réception synchronisation Yjs du serveur');
          // Utiliser 'remote' comme origine pour que handleYjsUpdate ne renvoie pas la mise à jour
          Y.applyUpdate(ydoc, new Uint8Array(data.state), 'remote');
        } catch (err) {
          console.error('[RichEditor] Erreur application sync Yjs:', err);
        }
      }
    });

    // Envoyer les mises à jour Yjs au serveur
    const handleYjsUpdate = (update: Uint8Array, origin: any) => {
      // Ne pas envoyer les mises à jour qui viennent du serveur (pour éviter les boucles)
      // L'extension Collaboration de Tiptap peut utiliser différentes origines
      // 'remote' = mise à jour reçue d'un autre client
      // 'server' = mise à jour du serveur
      // null/undefined = mise à jour locale de l'utilisateur
      // 'local' = mise à jour locale
      // 'prosemirror' = mise à jour de ProseMirror (locale)
      
      // Liste des origines à ignorer (mises à jour distantes)
      const remoteOrigins = ['server', 'remote'];
      
      // Vérifier si l'origine est une mise à jour distante
      const isRemote = origin && (
        remoteOrigins.includes(origin) || 
        (typeof origin === 'string' && origin.includes('remote')) ||
        (typeof origin === 'string' && origin.includes('server'))
      );
      
      if (isRemote) {
        console.log('[RichEditor] ⏭️ Mise à jour Yjs ignorée (origine distante):', origin);
        return;
      }
      
      // Envoyer toutes les autres mises à jour (locales, utilisateur, prosemirror, etc.)
      if (socket && socket.connected) {
        const originStr = origin ? String(origin) : 'local';
        console.log('[RichEditor] 📤 ENVOI mise à jour Yjs (origine):', originStr, 'taille:', update.length, 'bytes');
        try {
          socket.emit('yjs-update', {
            projectId,
            fileId: 'collaborative-file',
            update: Array.from(update)
          });
        } catch (err) {
          console.error('[RichEditor] ❌ Erreur lors de l\'envoi de la mise à jour Yjs:', err);
        }
      } else {
        console.warn('[RichEditor] ⚠️ Socket non connecté, mise à jour Yjs non envoyée (origine:', origin, ')');
      }
    };

    // S'abonner aux mises à jour Yjs
    // IMPORTANT: L'extension Collaboration de Tiptap déclenche automatiquement
    // des mises à jour Yjs quand l'utilisateur tape dans l'éditeur
    // Ces mises à jour sont capturées ici et envoyées via Socket.io
    console.log('[RichEditor] 📡 Abonnement aux mises à jour Yjs');
    
    // Créer une référence pour le handler pour pouvoir le retirer proprement
    const updateHandler = handleYjsUpdate;
    ydoc.on('update', updateHandler);
    
    // Observer les changements sur le fragment XML pour debug
    yXmlFragment.observe((event: any) => {
      console.log('[RichEditor] 🔄 Changement détecté dans yXmlFragment, type:', event.type);
    });
    
    // Log supplémentaire pour vérifier que les mises à jour sont bien capturées
    let updateCount = 0;
    const debugHandler = (update: Uint8Array, origin: any) => {
      updateCount++;
      if (updateCount % 10 === 0) {
        console.log(`[RichEditor] 📊 Total mises à jour Yjs capturées: ${updateCount}, dernière origine:`, origin);
      }
    };
    ydoc.on('update', debugHandler);

    // Écouter les changements de l'éditeur pour sauvegarder (avec debounce)
    let updateTimeout: NodeJS.Timeout;
    const handleEditorUpdate = () => {
      clearTimeout(updateTimeout);
      updateTimeout = setTimeout(() => {
        const html = editor.getHTML();
        if (onContentChangeRef.current) {
          onContentChangeRef.current(html);
        }
      }, 1000); // Debounce de 1s pour la sauvegarde
    };

    editor.on('update', handleEditorUpdate);

    // Charger le contenu initial si disponible (une seule fois)
    // Si Yjs est prêt mais que le document est vide, charger le contenu initial
    if (initialContent && initialContent.trim() && editor) {
      const currentContent = editor.getHTML();
      // Charger le contenu initial si l'éditeur est vide ou si le contenu est différent
      if (currentContent === '<p></p>' || (currentContent !== initialContent && initialContent.length > 0)) {
        console.log('[RichEditor] Chargement contenu initial, longueur:', initialContent.length);
        // Attendre un peu pour que Yjs soit complètement initialisé
        setTimeout(() => {
          if (editor && !editor.isDestroyed) {
            editor.commands.setContent(initialContent, { emitUpdate: false });
            // Si Yjs est prêt, synchroniser le contenu avec Yjs
            if (yjsReady && yXmlFragmentRef.current) {
              // Le contenu sera automatiquement synchronisé via Yjs
              console.log('[RichEditor] Contenu initial chargé et synchronisé avec Yjs');
            }
          }
        }, 500);
      }
    }

    return () => {
      clearTimeout(updateTimeout);
      editor.off('update', handleEditorUpdate);
      ydoc.off('update', updateHandler);
      ydoc.off('update', debugHandler);
      if (socketRef.current) {
        socketRef.current.emit('file-collab:leave', { projectId, roomId });
        socketRef.current.disconnect();
      }
    };
  }, [editor, yjsReady, projectId, initialContent]); // Dépendances minimales

  if (!yjsReady) {
    return <div className="p-6 flex items-center justify-center h-full">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
        <p className="text-gray-600">Initialisation de l'éditeur collaboratif...</p>
      </div>
    </div>;
  }

  if (!editor) {
    return <div className="p-6">Chargement de l'éditeur...</div>;
  }

  const toggleBold = () => editor.chain().focus().toggleBold().run();
  const toggleItalic = () => editor.chain().focus().toggleItalic().run();
  const toggleUnderline = () => editor.chain().focus().toggleUnderline().run();
  const toggleHeading1 = () => editor.chain().focus().toggleHeading({ level: 1 }).run();
  const toggleHeading2 = () => editor.chain().focus().toggleHeading({ level: 2 }).run();
  const toggleHeading3 = () => editor.chain().focus().toggleHeading({ level: 3 }).run();
  const toggleBulletList = () => editor.chain().focus().toggleBulletList().run();
  const toggleOrderedList = () => editor.chain().focus().toggleOrderedList().run();
  const insertTable = () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  const setLink = () => {
    const url = window.prompt('Entrez l\'URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };
  const addImage = () => {
    const url = window.prompt('Entrez l\'URL de l\'image:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50 flex-wrap">
        <button
          onClick={toggleBold}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-blue-100 text-blue-600' : ''}`}
          title="Gras"
        >
          <Bold className="h-4 w-4" />
        </button>
        <button
          onClick={toggleItalic}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-blue-100 text-blue-600' : ''}`}
          title="Italique"
        >
          <Italic className="h-4 w-4" />
        </button>
        <button
          onClick={toggleUnderline}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('underline') ? 'bg-blue-100 text-blue-600' : ''}`}
          title="Souligné"
        >
          <UnderlineIcon className="h-4 w-4" />
        </button>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <button
          onClick={toggleHeading1}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 1 }) ? 'bg-blue-100 text-blue-600' : ''}`}
          title="Titre 1"
        >
          <Heading1 className="h-4 w-4" />
        </button>
        <button
          onClick={toggleHeading2}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 2 }) ? 'bg-blue-100 text-blue-600' : ''}`}
          title="Titre 2"
        >
          <Heading2 className="h-4 w-4" />
        </button>
        <button
          onClick={toggleHeading3}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 3 }) ? 'bg-blue-100 text-blue-600' : ''}`}
          title="Titre 3"
        >
          <Heading3 className="h-4 w-4" />
        </button>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <button
          onClick={toggleBulletList}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-blue-100 text-blue-600' : ''}`}
          title="Liste à puces"
        >
          <List className="h-4 w-4" />
        </button>
        <button
          onClick={toggleOrderedList}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-blue-100 text-blue-600' : ''}`}
          title="Liste numérotée"
        >
          <ListOrdered className="h-4 w-4" />
        </button>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <button
          onClick={setLink}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('link') ? 'bg-blue-100 text-blue-600' : ''}`}
          title="Lien"
        >
          <LinkIcon className="h-4 w-4" />
        </button>
        <button
          onClick={addImage}
          className="p-2 rounded hover:bg-gray-200"
          title="Image"
        >
          <ImageIcon className="h-4 w-4" />
        </button>
        <button
          onClick={insertTable}
          className="p-2 rounded hover:bg-gray-200"
          title="Tableau"
        >
          <TableIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Editor Content */}
      <div className="flex-1 overflow-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default RichTextCollaborativeEditor;
